import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate } from 'react-router-dom';

import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/Button';
import { ErrorState, StateBlock } from '@/components/ui/StateBlock';
import { Surface } from '@/components/ui/Surface';
import { TableSkeleton } from '@/components/ui/TableSkeleton';
import { useCurrentUserQuery } from '@/features/user/queries';
import { describeError } from '@/lib/http';

import {
  fetchAdminUsers,
  updateAdminUser,
  type AdminUser,
} from './api';

function formatDate(value: string | null): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString('ru-RU');
}

export function AdminUsersPage() {
  const currentUser = useCurrentUserQuery();
  const queryClient = useQueryClient();
  const usersQuery = useQuery({
    queryKey: ['admin-users'],
    queryFn: ({ signal }) => fetchAdminUsers(signal),
    enabled: currentUser.data?.role === 'ADMIN',
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { role?: 'USER' | 'ADMIN'; isActive?: boolean } }) =>
      updateAdminUser(id, patch),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  if (currentUser.isPending) {
    return <StateBlock title="Загрузка…" description="Проверяем права доступа" />;
  }

  if (currentUser.data?.role !== 'ADMIN') {
    return <Navigate to="/transcriptions" replace />;
  }

  const items = usersQuery.data?.items ?? [];

  return (
    <div className="list-page">
      <PageHeader
        title="Пользователи"
        description="Управление ролями и доступом. Удаление встреч — в реестре расшифровок."
      />

      {usersQuery.isPending ? <TableSkeleton rows={6} /> : null}

      {usersQuery.isError ? (
        <ErrorState
          title="Не удалось загрузить пользователей"
          details={describeError(usersQuery.error)}
          onRetry={() => {
            void usersQuery.refetch();
          }}
        />
      ) : null}

      {updateMutation.isError ? (
        <div className="notice notice--danger" role="alert">
          <p className="notice__title">Не удалось сохранить изменения</p>
          <p className="notice__text">{describeError(updateMutation.error)}</p>
        </div>
      ) : null}

      {usersQuery.isSuccess ? (
        <Surface>
          <div className="data-table-wrapper data-table-wrapper--unclipped">
            <table className="data-table">
              <caption className="visually-hidden">Пользователи AXIUM Meet</caption>
              <thead>
                <tr>
                  <th scope="col">Пользователь</th>
                  <th scope="col">Провайдер</th>
                  <th scope="col">Роль</th>
                  <th scope="col">Статус</th>
                  <th scope="col">Встречи</th>
                  <th scope="col">Обновлён</th>
                  <th scope="col">
                    <span className="visually-hidden">Действия</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((user) => (
                  <AdminUserRow
                    key={user.id}
                    user={user}
                    currentUserId={currentUser.data?.id}
                    busy={updateMutation.isPending}
                    onChangeRole={(role) =>
                      updateMutation.mutate({ id: user.id, patch: { role } })
                    }
                    onToggleActive={() =>
                      updateMutation.mutate({
                        id: user.id,
                        patch: { isActive: !user.isActive },
                      })
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Surface>
      ) : null}
    </div>
  );
}

function AdminUserRow({
  user,
  currentUserId,
  busy,
  onChangeRole,
  onToggleActive,
}: {
  user: AdminUser;
  currentUserId: string | null | undefined;
  busy: boolean;
  onChangeRole: (role: 'USER' | 'ADMIN') => void;
  onToggleActive: () => void;
}) {
  const isSelf = user.id === currentUserId;

  return (
    <tr>
      <td>
        <div className="data-table__name">{user.displayName}</div>
        <div className="data-table__sub">{user.email}</div>
      </td>
      <td className="data-table__muted">{user.authProvider}</td>
      <td>
        <select
          className="field__control"
          value={user.role}
          disabled={busy || isSelf}
          aria-label={`Роль ${user.displayName}`}
          onChange={(event) => onChangeRole(event.target.value as 'USER' | 'ADMIN')}
        >
          <option value="USER">USER</option>
          <option value="ADMIN">ADMIN</option>
        </select>
      </td>
      <td className="data-table__muted">{user.isActive ? 'Активен' : 'Выключен'}</td>
      <td>
        {user.transcriptionsCount > 0 ? (
          <Link to={`/transcriptions?createdById=${encodeURIComponent(user.id)}`}>
            {user.transcriptionsCount}
          </Link>
        ) : (
          <span className="data-table__muted">0</span>
        )}
      </td>
      <td className="data-table__muted data-table__nowrap">{formatDate(user.updatedAt)}</td>
      <td className="data-table__actions">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={busy || isSelf}
          onClick={onToggleActive}
        >
          {user.isActive ? 'Выключить' : 'Включить'}
        </Button>
      </td>
    </tr>
  );
}
